import React, { useCallback, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  Box,
  FormControlLabel,
  Switch,
  Chip
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { ThemeProvider } from '@material-ui/styles';
import customTheme from '../../../customTheme';
import SvgUpload from '../../Signet/Studio/SvgUpload';
import { useDesignPatternStudio } from '../DesignPatternStudioContext';
import { ACTIONS } from '../reducers/studioReducer';
import useDesignPatternAPI from '../hooks/useDesignPatternAPI';
import ApprovalMapping from '../components/ApprovalMapping';
import { useHistory } from 'react-router';
import ManualWarpModal from '../components/ManualWarpModal';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
    margin: '0 auto',
  },
  title: {
    marginBottom: theme.spacing(3),
    paddingLeft: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  menuItem: {
    padding: theme.spacing(0.5, 2),
    width: '100%',
  },
}));

const StudioMain = () => {
  const classes = useStyles();
  const { state, setFile, dispatch } = useDesignPatternStudio();
  const { loading, fetchDesignPatternData } = useDesignPatternAPI();
  const history = useHistory();


  const { selectedMaterials, isAllMaterialsSelected } = useMemo(() => {
    const selected = state.mappingData?.materials?.filter((m) => m.checked) || [];
    const allMaterials = state.mappingData?.materials || [];
    const isAllMaterialsSelected = allMaterials.length > 0 && selected.length === allMaterials.length;

    return { selectedMaterials: selected, isAllMaterialsSelected: isAllMaterialsSelected };

  }, [state.mappingData?.materials]);

  const { selectedColors, isAllColorsSelected } = useMemo(() => {
    const selected = state.mappingData?.colors?.filter((c) => c.checked) || [];
    const allColors = state.mappingData?.colors || [];
    const isAllColorsSelected = allColors.length > 0 && selected.length === allColors.length;

    return { selectedColors: selected, isAllColorsSelected: isAllColorsSelected };

  }, [state.mappingData?.colors]);

  const handleDesignPatternChange = (event, value) => {
    if(state.designPatternId?.sku_code !== value?.sku_code) {
      dispatch({ type: ACTIONS.SET_DESIGN_PATTERN_ID, payload: value });
      dispatch({ type: ACTIONS.SET_INSIDE_FEATURE, payload: false });
    }

    if(!!value) {
      fetchDesignPatternData(value.sku_code);
    }
  };

  const handleMaterialChange = (event, selectedMaterials) => {
    const selectedMaterialIds = new Set(selectedMaterials.map((material) => material.id));
    const updatedData = {
      ...state.mappingData,
      materials: state.mappingData.materials.map((material) => ({
        ...material,
        checked: selectedMaterialIds.has(material.id),
      })),
    };

    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedData });
  };

  const handleSelectAllMaterials = (checked) => () => {
    const updatedData = {
      ...state.mappingData,
      materials: state.mappingData.materials.map((material) => ({
        ...material,
        checked: checked,
      })),
    };
    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedData });
  };

  const handleSelectAllColors = (checked) => () => {
    const updatedData = {
      ...state.mappingData,
      colors: state.mappingData.colors.map((color) => ({
        ...color,
        checked: checked,
      })),
    };
    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedData });
  };

  const handleColorChange = (event, selectedColors) => {
    const selectedColorIds = new Set(selectedColors.map((color) => color.id));

    const updatedData = {
      ...state.mappingData,
      colors: state.mappingData.colors.map((color) => ({
        ...color,
        checked: selectedColorIds.has(color.id),
      })),
    };

    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedData });
  };

  const handleSubmit = () => {
    if (!state.svgFile || !state.designPatternId || !state.mappingData) {
      return;
    }
    history.push('/design-pattern-studio/review');
  };

  // Handle file upload
  const handleSvgChange = useCallback((file) => {
    setFile(file);
    // Reset all manual warp related data when SVG changes
    dispatch({ type: ACTIONS.RESET_MANUAL_WARP_DATA });
  }, [setFile, dispatch]);

  const handleInsideFeatureChange = (event) => {
    dispatch({ type: ACTIONS.SET_INSIDE_FEATURE, payload: event.target.checked });
    fetchDesignPatternData(state.designPatternId?.sku_code, event.target.checked ? 1 : 0);
  };

  return (
    <ThemeProvider theme={customTheme}>
      <div className={classes.root}>
        <Typography variant="h4" className={classes.title}>
          Design Pattern Studio
        </Typography>
        <Paper className={classes.paper}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8} direction="column">
              <Box>
                <Typography variant="h6" className={classes.sectionTitle}>Select Design Pattern</Typography>
                <Autocomplete
                  id="design-pattern-autocomplete"
                  className={classes.dropdown}
                  value={state.designPatternId}
                  onChange={handleDesignPatternChange}
                  options={state.designPatterns || []}
                  getOptionLabel={(option) => option?.name || ''}
                  filterOptions={(options, { inputValue }) => {
                    const searchTerms = inputValue.toLowerCase().split(' ');
                    return options.filter((option) => {
                      const searchString = `${option?.name || ''}`.toLowerCase();
                      return searchTerms.every((term) => searchString.includes(term));
                    });
                  }}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search by design pattern name"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                  renderOption={(option) => (
                    <div>
                      <span>{option?.name || ''}</span>
                    </div>
                  )}
                />
              </Box>
              <Box my={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={state.insideFeature}
                      color="primary"
                      onChange={handleInsideFeatureChange}
                      disabled={!state.designPatternId?.allow_inside_engraving}
                    />
                  }
                  label="Inside Feature"
                />
              </Box>
              <Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="span" className={classes.sectionTitle}>
                    Select Materials
                  </Typography>
                  {state.designPatternId && <div>
                    {isAllMaterialsSelected ? (
                        <Chip
                        icon={<CheckCircleIcon style={{ color: '#4caf50' }} />}
                        label="Selected"
                        size="small"
                        style={{
                          backgroundColor: '#e8f5e8',
                          color: '#2e7d32',
                          marginRight: '8px',
                          cursor: 'pointer'
                        }}
                        onClick={handleSelectAllMaterials(false)}
                      />
                    ) : (
                      <Chip
                        icon={<CheckCircleIcon style={{ color: '#ff9800' }} />}
                        label="Select All"
                        size="small"
                        style={{
                          backgroundColor: '#fff3e0',
                          color: '#e65100',
                          marginRight: '8px',
                          cursor: 'pointer'
                        }}
                        onClick={handleSelectAllMaterials(true)}
                      />
                    )}
                  </div>}
                </Box>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  disabled={!state?.mappingData?.materials?.length}
                  className={classes.dropdown}
                  value={selectedMaterials || []}
                  onChange={handleMaterialChange}
                  options={state?.mappingData?.materials || []}
                  getOptionLabel={(option) => option.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search by material name"
                      variant="outlined"
                    />
                  )}
                  renderOption={(option, { selected }) => (
                    <MenuItem className={classes.menuItem}>
                      <Checkbox checked={selected} color="primary" />
                      <ListItemText primary={option.name} />
                    </MenuItem>
                  )}
                />
              </Box>
              {!state.insideFeature && <Box mt={2}>
              <Box display="flex" justifyContent="space-between">
                  <Typography variant="span" className={classes.sectionTitle}>
                    Select Colors
                  </Typography>
                  {state.designPatternId && <div>
                    {isAllColorsSelected ? (
                        <Chip
                        icon={<CheckCircleIcon style={{ color: '#4caf50' }} />}
                        label="Selected"
                        size="small"
                        style={{
                          backgroundColor: '#e8f5e8',
                          color: '#2e7d32',
                          marginRight: '8px',
                          cursor: 'pointer'
                        }}
                        onClick={handleSelectAllColors(false)}
                      />
                    ) : (
                      <Chip
                        icon={<CheckCircleIcon style={{ color: '#ff9800' }} />}
                        label="Select All"
                        size="small"
                        style={{
                          backgroundColor: '#fff3e0',
                          color: '#e65100',
                          marginRight: '8px',
                          cursor: 'pointer'
                        }}
                        onClick={handleSelectAllColors(true)}
                      />
                    )}
                  </div>}
                </Box>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  disabled={!state?.mappingData?.colors?.length}
                  className={classes.dropdown}
                  value={selectedColors || []}
                  onChange={handleColorChange}
                  options={state?.mappingData?.colors || []}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search by color name"
                      variant="outlined"
                    />
                  )}
                  renderOption={(option, { selected }) => (
                    <MenuItem className={classes.menuItem}>
                      <Checkbox checked={selected} color="primary" />
                      <ListItemText primary={option.name} />
                    </MenuItem>
                  )}
                />
              </Box>}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" className={classes.sectionTitle}>
                Upload SVG
              </Typography>
              <SvgUpload file={state.svgFile} onFileChange={handleSvgChange} />
            </Grid>
          </Grid>
          {selectedMaterials?.length>0  && (state.insideFeature || selectedColors?.length>0) && state.mappingData?.profiles?.length>0 && (
            <>
              <Box mt={4} width="100%">
                <ApprovalMapping />
              </Box>
              <Box mt={4} display="flex" justifyContent="flex-end" width="100%">
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.submitButton}
                  onClick={handleSubmit}
                  disabled={!state.svgFile}
                >
                  Proceed
                </Button>
              </Box>
            </>
         )}
          {/* <ManualWarpModal
            open={true}
            onClose={() => {}}
          /> */}
        </Paper>
      </div>
    </ThemeProvider>
  );
};

export default StudioMain;